import { CSSProperties } from "react";

import robotoFontUrl from "roboto-fontface/fonts/roboto/Roboto-Regular.woff2";

import Size from "@app/types/map/Size";
import getContrastColor from "@app/util/colors/getContrastColor";
import { getTextWidth } from "@app/util/text";

const cssFromObject = (properties: CSSProperties | undefined): string => {
  return properties
    ? Object.keys(properties)
        .map(
          (key: string) => `${key.replace(/([A-Z])/g, "-$1").toLowerCase()}: ${properties[key]};`
        )
        .join("")
    : "";
};

const getSvgForLabel = (
  label: string,
  fillColor: string,
  opacity: number,
  options?: { textStyles?: CSSProperties }
): {
  anchor: Size;
  size: Size;
  svg: string;
} => {
  // making big padding to eliminate any text width miscalculations
  // since SVG bg is transparent anyway and we anchor it to the middle
  // users won't see this
  const padding = 24;
  const fontSize = 12;
  const lineHeight = 22;
  const font = `bold ${fontSize}px/${lineHeight}px "Roboto", sans-serif`;
  const textWidth = Math.ceil(getTextWidth(label, font, { letterSpacing: "1.4px" }));
  const size = [textWidth + padding * 2, fontSize * 2] as Size;

  const useBlackOutline = getContrastColor(fillColor) === "black";
  const colorMatrix = useBlackOutline
    ? `0 0 0 0 0, 0 0 0 0 0, 0 0 0 0 0, 0 0 0 ${opacity} 0`
    : `-1 0 0 0 1, 0 -1 0 0 1, 0 0 -1 0 1, 0 0 0 ${opacity} 0`;

  return {
    anchor: [size[0] / 2, size[1] / 2], // center of label
    size,
    svg: `<svg width="${size[0]}" height="${size[1]}" viewBox="0 0 ${size[0]} ${
      size[1]
    }" xmlns="http://www.w3.org/2000/svg">
      <style>
        @font-face {
          font-family: Roboto;
          src: url(${robotoFontUrl})
        }

        text {
          font-family: Roboto, sans-serif;
          letter-spacing: 0.0875rem;
          ${cssFromObject(options?.textStyles)}
        }
      </style>
      <defs>
        <filter id="outlineEffect" color-interpolation-filters="sRGB">
          <feMorphology in="SourceAlpha" result="MORPH" operator="dilate" radius="1" />
          <feColorMatrix in="MORPH" result="OUTLINED" type="matrix" values="${colorMatrix}"/>
          <feMerge>
            <feMergeNode in="OUTLINED"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <g>
        <text 
          x="${padding}"
          y="${fontSize}"
          font-size="${fontSize}px" 
          font-weight="bold" 
          filter="url(#outlineEffect)"
          fill="${fillColor}"
          fill-opacity="${opacity}"
        >
          ${label}
        </text>
      </g>
    </svg>`,
  };
};

export default getSvgForLabel;
